import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import {
  Box,
  Button,
  Chip,
  Divider,
  Grid,
  Link,
  Paper,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import { PimoReactComponent } from "@pimo/pimo-app-builder";
import { getLocalizedMessage, SupportedLanguage } from "@pimo/pimo-l10n";

import { PRICING_PAGE_URL } from "../constants";
import {
  ADDITIONAL_TOOL_PRICE_EUR,
  App,
  BillingAddress,
  Invoice,
  Plan,
  Subscription,
} from "../types/entities";
import { InvoiceHistoryList } from "./invoice-history-list";

export interface PricingTabState {
  plans: Plan[];
  subscription?: Subscription | null;
  apps: App[];
  totalUsers: number;
  billingAddress?: BillingAddress | null;
  /**
   * Invoice history from `GET /subscriptions/invoices` (#2791/#2794).
   * Rendered read-only for both the Owner and an Admin — see
   * `InvoiceHistoryList`.
   */
  invoices?: Invoice[];
  isOwner: boolean;
  language: SupportedLanguage;
}

export type PricingTabEvent =
  | "edit-billing-address"
  | "open-cancel-subscription-dialog"
  | "open-update-payment-method-dialog";

function formatNextInvoice(): string {
  const next = new Date();
  next.setMonth(next.getMonth() + 1);
  next.setDate(1);
  return next.toLocaleDateString("en-US", {
    month: "long",
    day: "numeric",
  });
}

/** Formats `subscription.cancelAt`/`periodEndDate` for display — unlike
 * `formatNextInvoice` (a pinned, pre-existing bug, see #2297), this is new
 * code so it respects the given `language` from the start rather than
 * hard-coding `en-US`. */
export function formatPeriodEndDate(
  isoDate: string,
  language: SupportedLanguage
): string {
  const locale = language === "de" ? "de-DE" : "en-US";
  return new Date(isoDate).toLocaleDateString(locale, {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
}

const PILL_GREEN_BG = "#dff5e1";
const PILL_GREEN_FG = "#2e7d32";
const ROW_BORDER = "#e6e6e6";

function PlanPill({ children }: { children: React.ReactNode }) {
  return (
    <Chip
      icon={<CheckCircleIcon sx={{ color: `${PILL_GREEN_FG} !important` }} />}
      label={children}
      sx={{
        bgcolor: PILL_GREEN_BG,
        color: PILL_GREEN_FG,
        fontWeight: 500,
        borderRadius: 999,
        height: 28,
        fontSize: "0.8rem",
      }}
    />
  );
}

/** The "Cancellation scheduled" status pill (#2796, UX spec flow d): amber
 * `theme.palette.warning` tones (never the green "active" styling, to avoid
 * implying nothing is changing) with its own icon, not color alone, per the
 * spec's non-color-status-signaling requirement. */
function CancellationScheduledPill({
  children,
}: {
  children: React.ReactNode;
}) {
  const theme = useTheme();
  return (
    <Chip
      icon={
        <WarningAmberIcon
          sx={{ color: `${theme.palette.warning.dark} !important` }}
        />
      }
      label={children}
      data-testid="cancellation-scheduled-pill"
      sx={{
        bgcolor: theme.palette.warning.light,
        color: theme.palette.warning.dark,
        fontWeight: 500,
        borderRadius: 999,
        height: 28,
        fontSize: "0.8rem",
      }}
    />
  );
}

/** The "Past due" status pill (#2795, UX spec flow e/f): same amber
 * `theme.palette.warning` tones and icon-not-color-alone treatment as
 * `CancellationScheduledPill`, so the pricing tab and the global
 * `PastDuePaymentBanner` tell a consistent story. Kept as its own sibling
 * component (one per status, matching the existing convention) rather than
 * generalizing into a single `tone`-driven pill — the spec notes this is
 * an open question ("can be a prop on a generalized status pill"); with
 * only two amber variants so far, the duplication is small enough that a
 * generalization felt like premature abstraction for this task, but it's a
 * reasonable follow-up if a third status pill is added later. */
function PastDuePill({ children }: { children: React.ReactNode }) {
  const theme = useTheme();
  return (
    <Chip
      icon={
        <WarningAmberIcon
          sx={{ color: `${theme.palette.warning.dark} !important` }}
        />
      }
      label={children}
      data-testid="past-due-pill"
      sx={{
        bgcolor: theme.palette.warning.light,
        color: theme.palette.warning.dark,
        fontWeight: 500,
        borderRadius: 999,
        height: 28,
        fontSize: "0.8rem",
      }}
    />
  );
}

/** The "Canceled" status pill (#2794, human-readable status requirement):
 * neutral gray tones (never the green "active" styling, and never the
 * amber "needs attention" tones used by `PastDuePill`/
 * `CancellationScheduledPill` — a subscription that has already ended is
 * neither a problem to fix nor a change in progress). Shown for a
 * subscription whose `status` is `'canceled'` — i.e. the Stripe webhook
 * has already confirmed `customer.subscription.deleted` — as opposed to
 * `cancel_scheduled`, which still shows `CancellationScheduledPill` above
 * because the plan remains active until the period end. */
function CanceledPill({ children }: { children: React.ReactNode }) {
  const theme = useTheme();
  return (
    <Chip
      label={children}
      data-testid="canceled-pill"
      sx={{
        bgcolor: theme.palette.grey[200],
        color: theme.palette.text.secondary,
        fontWeight: 500,
        borderRadius: 999,
        height: 28,
        fontSize: "0.8rem",
      }}
    />
  );
}

export const PricingTab: PimoReactComponent<
  PricingTabState,
  PricingTabEvent,
  unknown
> = ({
  fireEvent,
  plans,
  subscription,
  apps,
  totalUsers,
  billingAddress,
  invoices = [],
  isOwner,
  language,
}) => {
  const currentPlan = subscription
    ? plans.find((p) => p.key === subscription.planKey)
    : undefined;

  const paidApps = apps.filter((a) => a.activationStatus === "active_paid");
  const includedApps = apps.filter(
    (a) => a.activationStatus === "active_included"
  );

  const baseCost = currentPlan?.basePriceEur ?? 0;
  const additionalCost = paidApps.length * ADDITIONAL_TOOL_PRICE_EUR;
  const totalMonthlyCost = baseCost + additionalCost;
  const userLimit = currentPlan?.userLimit ?? 0;

  if (!subscription) {
    return (
      <Box>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          sx={{ mb: 2 }}
        >
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            {getLocalizedMessage(
              language,
              "homePortalPage.tabs.pricePlans.pageTitle"
            )}
          </Typography>
        </Stack>
        <Paper variant="outlined" sx={{ p: 3, borderRadius: 2 }}>
          <Typography variant="body2" color="text.secondary">
            {getLocalizedMessage(
              language,
              "homePortalPage.tabs.pricePlans.noPlanSelected"
            )}
          </Typography>
        </Paper>
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
        {getLocalizedMessage(
          language,
          "homePortalPage.tabs.pricePlans.pageTitle"
        )}
      </Typography>

      <Grid container spacing={3}>
        <Grid item xs={12} md={8}>
          <Paper
            variant="outlined"
            sx={{
              borderRadius: 2,
              borderColor: ROW_BORDER,
              overflow: "hidden",
            }}
          >
            <CostRow
              label={getLocalizedMessage(
                language,
                "homePortalPage.tabs.pricePlans.pricing.title"
              )}
              helper={
                subscription.status === "pending_change" &&
                subscription.requestedPlanKey
                  ? getLocalizedMessage(
                      language,
                      "homePortalPage.tabs.pricePlans.pricing.changePendingReview",
                      {
                        newPlan:
                          plans.find(
                            (p) => p.key === subscription.requestedPlanKey
                          )?.name ?? subscription.requestedPlanKey,
                      }
                    )
                  : undefined
              }
            >
              {subscription.status === "cancel_scheduled" &&
              subscription.cancelAt ? (
                <CancellationScheduledPill>
                  {getLocalizedMessage(
                    language,
                    "homePortalPage.tabs.pricePlans.pricing.cancellationScheduled",
                    {
                      periodEndDate: formatPeriodEndDate(
                        subscription.cancelAt,
                        language
                      ),
                    }
                  )}
                </CancellationScheduledPill>
              ) : subscription.status === "past_due" ? (
                <PastDuePill>
                  {getLocalizedMessage(
                    language,
                    "homePortalPage.tabs.pricePlans.pricing.pastDue"
                  )}
                </PastDuePill>
              ) : subscription.status === "canceled" ? (
                <CanceledPill>
                  {getLocalizedMessage(
                    language,
                    "homePortalPage.tabs.pricePlans.pricing.canceled"
                  )}
                </CanceledPill>
              ) : (
                <PlanPill>
                  {currentPlan?.name ?? subscription.planKey}:{" "}
                  {getLocalizedMessage(
                    language,
                    "homePortalPage.tabs.pricePlans.pricing.basePrice",
                    { price: currentPlan?.basePriceEur ?? 0 }
                  )}
                </PlanPill>
              )}
            </CostRow>
            <Divider />
            <CostRow
              label={getLocalizedMessage(
                language,
                "homePortalPage.tabs.pricePlans.pricing.includedApps"
              )}
              helper={getLocalizedMessage(
                language,
                "homePortalPage.tabs.pricePlans.pricing.includedAppsHelper"
              )}
            >
              <Typography variant="body2">
                {getLocalizedMessage(
                  language,
                  "homePortalPage.tabs.pricePlans.pricing.includedWithPrice",
                  { included: includedApps.length, additionalAppPrice: 0 }
                )}
              </Typography>
            </CostRow>
            <Divider />
            <CostRow
              label={getLocalizedMessage(
                language,
                "homePortalPage.tabs.pricePlans.pricing.paidApps"
              )}
              helper={getLocalizedMessage(
                language,
                "homePortalPage.tabs.pricePlans.pricing.paidAppsHelper"
              )}
            >
              <Typography variant="body2">
                {getLocalizedMessage(
                  language,
                  "homePortalPage.tabs.pricePlans.pricing.extraAppsCost",
                  {
                    extraApps: paidApps.length,
                    additionalAppPrice: ADDITIONAL_TOOL_PRICE_EUR,
                  }
                )}
              </Typography>
            </CostRow>
            <Divider />
            <CostRow
              label={getLocalizedMessage(
                language,
                "homePortalPage.tabs.pricePlans.pricing.totalUsers"
              )}
              helper={getLocalizedMessage(
                language,
                "homePortalPage.tabs.pricePlans.pricing.totalUsersHelper"
              )}
            >
              <Typography variant="body2">
                {getLocalizedMessage(
                  language,
                  "homePortalPage.tabs.pricePlans.pricing.availableUsers",
                  {
                    totalUsers,
                    userLimit,
                    availableSeats: Math.max(userLimit - totalUsers, 0),
                  }
                )}
              </Typography>
            </CostRow>
            <Divider />
            <Box sx={{ px: 3, py: 2 }}>
              <Link
                href={PRICING_PAGE_URL}
                target="_blank"
                rel="noopener"
                underline="hover"
                sx={{
                  color: "primary.main",
                  fontWeight: 500,
                  fontSize: "0.875rem",
                }}
              >
                {getLocalizedMessage(
                  language,
                  "homePortalPage.tabs.pricePlans.pricing.viewFullPricingDetails"
                )}
              </Link>
            </Box>
          </Paper>

          {/* De-emphasized, low-frequency destructive action — plain-text
           * styling via `useTheme()`'s `text.secondary`, placed below the
           * main plan card so it never competes with "Change Plan" as a
           * peer CTA (#2785 UX spec, flow d). Hidden once a cancellation is
           * already scheduled or the subscription has ended — there is no
           * "undo" to request again, and re-submitting a cancel while one
           * is already scheduled has nothing useful to do. */}
          {isOwner &&
          subscription.status !== "cancel_scheduled" &&
          subscription.status !== "canceled" ? (
            <Box sx={{ mt: 2, textAlign: "left" }}>
              <CancelSubscriptionLink
                onClick={() => fireEvent?.("open-cancel-subscription-dialog")}
              >
                {getLocalizedMessage(
                  language,
                  "homePortalPage.tabs.pricePlans.actions.cancelSubscription"
                )}
              </CancelSubscriptionLink>
            </Box>
          ) : null}

          <Paper
            variant="outlined"
            sx={{ borderRadius: 2, borderColor: ROW_BORDER, mt: 3 }}
          >
            <Stack
              direction={{ xs: "column", sm: "row" }}
              sx={{ p: 3, gap: 3 }}
            >
              <Box sx={{ minWidth: 140 }}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {getLocalizedMessage(
                    language,
                    "homePortalPage.tabs.pricePlans.billing.title"
                  )}
                </Typography>
              </Box>
              <Stack
                component="ul"
                spacing={0.5}
                sx={{
                  pl: 2,
                  my: 0,
                  color: "text.secondary",
                  fontSize: "0.85rem",
                  flex: 1,
                }}
              >
                <li>
                  {getLocalizedMessage(
                    language,
                    "homePortalPage.tabs.pricePlans.billing.li1"
                  )}
                </li>
                <li>
                  {getLocalizedMessage(
                    language,
                    "homePortalPage.tabs.pricePlans.billing.li2"
                  )}
                </li>
                <li>
                  {getLocalizedMessage(
                    language,
                    "homePortalPage.tabs.pricePlans.billing.li3"
                  )}
                </li>
                <li>
                  {getLocalizedMessage(
                    language,
                    "homePortalPage.tabs.pricePlans.billing.li4"
                  )}
                </li>
              </Stack>
            </Stack>
          </Paper>

          <Paper
            variant="outlined"
            sx={{ borderRadius: 2, borderColor: ROW_BORDER, mt: 3 }}
          >
            <Stack
              direction={{ xs: "column", sm: "row" }}
              sx={{ p: 3, gap: 3 }}
            >
              <Box sx={{ minWidth: 140 }}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {getLocalizedMessage(
                    language,
                    "homePortalPage.tabs.pricePlans.invoicing.title"
                  )}
                </Typography>
              </Box>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ flex: 1 }}
              >
                {getLocalizedMessage(
                  language,
                  "homePortalPage.tabs.pricePlans.invoicing.description"
                )}
              </Typography>
            </Stack>
          </Paper>

          {/* Invoice history (#2791/#2794): read-only for both the Owner
           * and an Admin (see `InvoiceHistoryList`'s own doc comment) —
           * sourced live from Stripe via `GET /subscriptions/invoices`,
           * never cached/persisted locally. */}
          <Paper
            variant="outlined"
            sx={{ borderRadius: 2, borderColor: ROW_BORDER, mt: 3 }}
          >
            <Box sx={{ p: 3 }}>
              <Typography
                variant="body2"
                sx={{ fontWeight: 600, mb: invoices.length === 0 ? 0 : 2 }}
              >
                {getLocalizedMessage(
                  language,
                  "homePortalPage.tabs.pricePlans.invoiceHistory.title"
                )}
              </Typography>
              <InvoiceHistoryList invoices={invoices} language={language} />
            </Box>
          </Paper>

          <Paper
            variant="outlined"
            sx={{ borderRadius: 2, borderColor: ROW_BORDER, mt: 3 }}
          >
            <Stack
              direction={{ xs: "column", sm: "row" }}
              sx={{ p: 3, gap: 3 }}
            >
              <Box sx={{ minWidth: 140 }}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {getLocalizedMessage(
                    language,
                    "homePortalPage.tabs.pricePlans.billingDetails.title"
                  )}
                </Typography>
              </Box>
              <Box sx={{ flex: 1 }}>
                {billingAddress ? (
                  <Typography variant="body2" color="text.secondary">
                    {billingAddress.companyName}
                    <br />
                    {billingAddress.line1}
                    {billingAddress.line2 ? `, ${billingAddress.line2}` : ""}
                    <br />
                    {billingAddress.postalCode} {billingAddress.city}
                  </Typography>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    {getLocalizedMessage(
                      language,
                      "homePortalPage.tabs.pricePlans.billingDetails.noBillingAddress"
                    )}
                  </Typography>
                )}
              </Box>
              <Box
                sx={{
                  minWidth: 180,
                  textAlign: "right",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "flex-end",
                }}
              >
                {isOwner ? (
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => fireEvent?.("edit-billing-address")}
                  >
                    {getLocalizedMessage(
                      language,
                      "homePortalPage.tabs.pricePlans.billingDetails.edit"
                    )}
                  </Button>
                ) : null}
              </Box>
            </Stack>
          </Paper>

          {/* Payment method (#2795 follow-up): lets the Owner replace the
           * stored card at any time, not only when a `PastDuePaymentBanner`
           * is shown for a `past_due` subscription. Reuses the same
           * "open-update-payment-method-dialog" event/dialog as that
           * banner — `createPaymentMethodUpdateSetupIntent` has no
           * status restriction on the backend, it is purely Owner-gated. */}
          <Paper
            variant="outlined"
            sx={{ borderRadius: 2, borderColor: ROW_BORDER, mt: 3 }}
          >
            <Stack
              direction={{ xs: "column", sm: "row" }}
              sx={{ p: 3, gap: 3 }}
            >
              <Box sx={{ minWidth: 140 }}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {getLocalizedMessage(
                    language,
                    "homePortalPage.tabs.pricePlans.paymentMethod.title"
                  )}
                </Typography>
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  {getLocalizedMessage(
                    language,
                    "homePortalPage.tabs.pricePlans.paymentMethod.description"
                  )}
                </Typography>
              </Box>
              <Box
                sx={{
                  minWidth: 180,
                  textAlign: "right",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "flex-end",
                }}
              >
                {isOwner ? (
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() =>
                      fireEvent?.("open-update-payment-method-dialog")
                    }
                  >
                    {getLocalizedMessage(
                      language,
                      "homePortalPage.tabs.pricePlans.paymentMethod.update"
                    )}
                  </Button>
                ) : null}
              </Box>
            </Stack>
          </Paper>
        </Grid>

        <Grid item xs={12} md={4}>
          <Paper
            variant="outlined"
            sx={{ p: 3, borderRadius: 2, borderColor: ROW_BORDER }}
          >
            <Stack direction="row" alignItems="baseline" spacing={1}>
              <Typography
                variant="h4"
                sx={{ fontWeight: 700, color: "primary.main", lineHeight: 1 }}
              >
                {totalMonthlyCost}
              </Typography>
              <Typography
                variant="subtitle1"
                sx={{ color: "primary.main" }}
              ></Typography>
            </Stack>
            <Typography
              variant="caption"
              display="block"
              color="text.secondary"
              sx={{ mt: 0.5 }}
            >
              {getLocalizedMessage(
                language,
                "homePortalPage.tabs.pricePlans.monthlyCosts.totalMonthlyCosts"
              )}
            </Typography>
            <Divider sx={{ my: 2 }} />
            <Typography
              variant="caption"
              display="block"
              color="text.secondary"
            >
              {getLocalizedMessage(
                language,
                "homePortalPage.tabs.pricePlans.monthlyCosts.basedOn"
              )}
              <Box
                component="span"
                sx={{ color: "primary.main", fontWeight: 600 }}
              >
                {paidApps.length}
              </Box>{" "}
              {getLocalizedMessage(
                language,
                `homePortalPage.tabs.pricePlans.monthlyCosts.${paidApps.length === 1 ? "paidTool" : "paidTools"}`
              )}
              {getLocalizedMessage(
                language,
                "homePortalPage.tabs.pricePlans.monthlyCosts.included",
                { included: includedApps.length }
              )}
            </Typography>
            <Typography
              variant="caption"
              display="block"
              color="text.secondary"
              sx={{ mt: 0.5 }}
            >
              {getLocalizedMessage(
                language,
                "homePortalPage.tabs.pricePlans.monthlyCosts.nextInvoice"
              )}{" "}
              <Box
                component="span"
                sx={{ color: "primary.main", fontWeight: 600 }}
              >
                {formatNextInvoice()}
              </Box>
            </Typography>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

/** The de-emphasized "Cancel subscription" entry point (#2796, UX spec flow
 * d): a plain-text button, muted via `theme.palette.text.secondary`, with a
 * real 44×44px-minimum touch target (MUI `Button`'s own default) rather
 * than a shrunk link — de-emphasis is color/weight only, per the spec's
 * accessibility notes, never a smaller hit area. */
function CancelSubscriptionLink({
  onClick,
  children,
}: {
  onClick: () => void;
  children: React.ReactNode;
}) {
  const theme = useTheme();
  return (
    <Button
      variant="text"
      onClick={onClick}
      sx={{
        textTransform: "none",
        color: theme.palette.text.secondary,
        fontWeight: 400,
        px: 1,
        "&:hover": {
          backgroundColor: "transparent",
          textDecoration: "underline",
        },
      }}
    >
      {children}
    </Button>
  );
}

function CostRow({
  label,
  helper,
  children,
}: {
  label: string;
  helper?: string;
  children: React.ReactNode;
}) {
  return (
    <Stack
      direction="row"
      justifyContent="space-between"
      alignItems="center"
      sx={{ px: 3, py: 2 }}
    >
      <Box>
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          {label}
        </Typography>
        {helper ? (
          <Typography variant="caption" color="text.secondary">
            {helper}
          </Typography>
        ) : null}
      </Box>
      <Box>{children}</Box>
    </Stack>
  );
}
